/**
 * Shared JSON storage for React and imperative callers. Import from
 * @sfc/ui/local-storage to avoid pulling in React components.
 *
 * getLocalStorageValue(key, fallback, { validate }) reads a validated value;
 * setLocalStorageValue(key, value) persists it and notifies subscribers.
 * Existing wire formats and durability-sensitive callers use rawLocalStorage.
 * Defaults are caller-owned. Failed writes never create in-memory state.
 */
import type { ZodSchema } from "zod";

const LOCAL_STORAGE_CHANGE_EVENT = "sfc:local-storage-change";

export type LocalStorageValidator<T> = (value: unknown) => value is T;

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

export const isBooleanRecord = (
  value: unknown,
): value is Record<string, boolean> =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).every((item) => typeof item === "boolean");

function localStorageOrNull(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Compatibility boundary for existing serialized formats and Storage-shaped
 * consumers (queues, caches). Values are never encoded or decoded here.
 * Reads, writes, removal and enumeration throw on failure so callers can
 * distinguish a missing record from unavailable storage and retry quota errors.
 * Prefer the validated JSON helpers for new preferences.
 */
export const rawLocalStorage = {
  // Like `typeof localStorage !== "undefined"`: absence is false, but a
  // browser security error still throws for the caller to handle.
  isSupported(): boolean {
    return typeof requireLocalStorage() !== "undefined";
  },
  getItem(key: string): string | null {
    return requireLocalStorage().getItem(key);
  },
  setItem(key: string, value: string): void {
    requireLocalStorage().setItem(key, value);
    emitLocalStorageChange(key);
  },
  removeItem(key: string): void {
    requireLocalStorage().removeItem(key);
    emitLocalStorageChange(key);
  },
  key(index: number): string | null {
    return requireLocalStorage().key(index);
  },
  get length(): number {
    return requireLocalStorage().length;
  },
};

function requireLocalStorage(): Storage {
  // Resolve lazily: importing this module must work during SSR. The global
  // fallback also supports DOM-less consumers with an injected Storage.
  return typeof window === "undefined"
    ? globalThis.localStorage
    : window.localStorage;
}

export function isLocalStorageAvailable(): boolean {
  return localStorageOrNull() != null;
}

export function getLocalStorageSnapshot(key: string): string | null {
  const storage = localStorageOrNull();

  if (storage == null) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * With no caller-supplied validator, preserve the broad runtime shape implied
 * by the default. This catches the dangerous cases (an object where a list was
 * expected, a string where a boolean was expected) without pretending generic
 * TypeScript information exists at runtime.
 */
function matchesDefaultShape(value: unknown, fallback: unknown): boolean {
  if (fallback == null) {
    return true;
  }

  if (Array.isArray(fallback)) {
    return Array.isArray(value);
  }

  if (typeof fallback === "object") {
    return value != null && typeof value === "object" && !Array.isArray(value);
  }

  return typeof value === typeof fallback;
}

export function parseStoredValue<T>(
  raw: string | null,
  fallback: T,
  validate?: LocalStorageValidator<T>,
  schema?: ZodSchema<T>,
): T {
  if (raw == null) {
    return fallback;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (schema != null) {
      return schema.parse(parsed);
    }

    const valid = validate?.(parsed) ?? matchesDefaultShape(parsed, fallback);
    return valid ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

export function parseLocalStorageValue<T>(
  raw: string | null,
  fallback: T,
  validate?: LocalStorageValidator<T>,
): T {
  return parseStoredValue(raw, fallback, validate);
}

export function subscribeLocalStorage(
  key: string,
  onStoreChange: () => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    const localStorage = localStorageOrNull();

    if (event.storageArea != null && event.storageArea !== localStorage) {
      return;
    }

    // A null key means another tab called localStorage.clear().
    if (event.key == null || event.key === key) {
      onStoreChange();
    }
  };

  const onLocalStorageChange = (event: Event) => {
    if ((event as CustomEvent<string>).detail === key) {
      onStoreChange();
    }
  };

  window.addEventListener("storage", onStorage);
  window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, onLocalStorageChange);

  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(
      LOCAL_STORAGE_CHANGE_EVENT,
      onLocalStorageChange,
    );
  };
}

function emitLocalStorageChange(key: string): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<string>(LOCAL_STORAGE_CHANGE_EVENT, { detail: key }),
  );
}

function removeStoredValue(storage: Storage, key: string): boolean {
  try {
    storage.removeItem(key);
    return true;
  } catch (error) {
    console.warn("[storage] remove failed", { key, error: String(error) });
    return false;
  }
}

function writeStoredValue(
  storage: Storage,
  key: string,
  value: unknown,
): boolean {
  try {
    const serialized = JSON.stringify(value);

    if (serialized == null) {
      return false;
    }

    storage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.warn("[storage] write failed", { key, error: String(error) });
    return false;
  }
}

/** Read JSON with the same fallback and validation rules as useLocalStorage. */
export function getLocalStorageValue<T>(
  key: string,
  fallback: T,
  options: { validate?: LocalStorageValidator<T>; schema?: ZodSchema<T> } = {},
): T {
  return parseStoredValue(
    getLocalStorageSnapshot(key),
    fallback,
    options.validate,
    options.schema,
  );
}

/** JSON write; null/undefined removes the key. Notify only after successful persistence.
 * Returns false when storage is unavailable or serialization/persistence fails.
 * Values must satisfy the caller's contract; validation happens on reads.
 */
export function setLocalStorageValue(key: string, value: unknown): boolean {
  const storage = localStorageOrNull();

  if (storage == null) {
    return false;
  }

  const persisted =
    value == null
      ? removeStoredValue(storage, key)
      : writeStoredValue(storage, key, value);

  if (persisted) {
    emitLocalStorageChange(key);
  }

  return persisted;
}
